import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { MaintenanceReportFilterModel } from 'src/app/models/maintenance/maintenance-report-filter.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { MaintenanceReportService } from 'src/app/services/maintenance/maintenance-report.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-repair-history',
  templateUrl: './repair-history.component.html',
  styleUrls: ['./repair-history.component.scss'],
  standalone: false
})
export class RepairHistoryComponent implements OnInit, AfterViewInit, OnDestroy {

  private readonly articleService = inject(ArticleService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly maintenanceReportService = inject(MaintenanceReportService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  formMaintenanceReportFilter: FormGroup = new FormGroup({});
  listArticle: any[] = [];
  nameWindow: string = 'Histórico de Reparaciones';
  projectName = ProjectName.projectName;

  async ngOnInit() {
    this.validateForm();
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getAllLists();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  changeArticle(articleId: number | null) {
    if (articleId == 0 || articleId == null) {
      this.formMaintenanceReportFilter.controls["articleId"].patchValue(0);
    }
    this.changeFilter();
  }

  async clear() {
    this.changeFilter();
    this.validateForm();
  }

  async exportExcel() {
    if (this.changeFilters) {
      try {
        const model = this.formMaintenanceReportFilter.value as MaintenanceReportFilterModel;
        const response = await firstValueFrom(this.maintenanceReportService.exportRepairHistory(model));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.nameWindow}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast('info', 'No se encontraron datos para exportar.');
    }
  }

  async getAllLists() {
    try {
      const articles = await firstValueFrom(this.articleService.getAllArticlesForSelectAsync());
      this.listArticle = articles;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast('info', 'No se encontraron datos para imprimir.');
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formMaintenanceReportFilter.valid) {
      this.showValidationMessages();
      return;
    }
    const model = this.formMaintenanceReportFilter.value as MaintenanceReportFilterModel;
    if (model.finalDate < model.initialDate) {
      this.toastHandlerService.showToast('warning', 'La fecha final no puede ser menor a la fecha inicial.');
      return;
    }
    try {
      this.dataReport = await firstValueFrom(this.maintenanceReportService.getRepairHistory(model));
      if (this.dataReport && this.dataReport.data?.length > 0) {
        this.changeFilters = true;
      } else {
        this.changeFilters = false;
        this.toastHandlerService.showToast('info', 'No se encontraron datos.');
      }
    } catch (error) {
      this.changeFilters = false;
      console.error("Error al consultar el reporte:", error);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      initialDate: {
        required: 'La fecha inicial es obligatoria.'
      },
      finalDate: {
        required: 'La fecha final es obligatoria.'
      },
      articleId: {
        notNumber: 'Debe seleccionar un Artículo válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formMaintenanceReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formMaintenanceReportFilter = this.formBuilder.group({
      initialDate: [moment().startOf('month').toDate(), [Validators.required]],
      finalDate: [moment().toDate(), [Validators.required]],
      articleId: [0, [CustomValidators.numberOnlyValidator()]]
    });
  }

  validateFormControls() {
    Object.keys(this.formMaintenanceReportFilter.controls).forEach((key) => {
      const control = this.formMaintenanceReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
  }

}